import { useState, useEffect } from 'react';
import { api } from '../utils/api';

const LogoUpload = () => {
  const [currentLogo, setCurrentLogo] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchCurrentLogo();
  }, []);

  const fetchCurrentLogo = async () => {
    try {
      const data = await api.get('/shul/');
      if (data.center_logo) {
        setCurrentLogo(data.center_logo);
      }
    } catch (error) {
      console.error('Error fetching current logo:', error);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Please select an image file (PNG, JPG, etc.)');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('File size must be less than 5MB');
        return;
      }

      setLogoFile(file);
      setErrorMessage('');

      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!logoFile) {
      setErrorMessage('Please select a logo file first');
      return;
    }

    setIsSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const formData = new FormData();
      formData.append('center_logo', logoFile);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api'}/shul/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload logo');
      }

      const data = await response.json();
      setCurrentLogo(data.center_logo);
      setSuccessMessage('Logo uploaded successfully!');
      setLogoFile(null);
      setLogoPreview(null);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error uploading logo:', error);
      setErrorMessage(error.message || 'Failed to upload logo. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Are you sure you want to remove the logo?')) {
      return;
    }

    setIsSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api'}/shul/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ remove_logo: 'true' })
      });

      if (!response.ok) {
        throw new Error('Failed to remove logo');
      }

      setCurrentLogo(null);
      setSuccessMessage('Logo removed successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error removing logo:', error);
      setErrorMessage('Failed to remove logo. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelPreview = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setErrorMessage('');
  };

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '1rem 1.25rem',
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      flexWrap: 'wrap'
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      minWidth: '120px'
    },
    logoPreview: {
      width: '60px',
      height: '60px',
      objectFit: 'contain',
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      padding: '4px',
      backgroundColor: '#f9fafb'
    },
    noLogoPlaceholder: {
      width: '60px',
      height: '60px',
      border: '2px dashed #d1d5db',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      color: '#9ca3af',
      backgroundColor: '#f9fafb'
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.5rem',
      marginLeft: 'auto',
      flexWrap: 'wrap'
    },
    button: {
      padding: '0.5rem 1rem',
      borderRadius: '6px',
      fontSize: '0.875rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s',
      border: 'none',
      whiteSpace: 'nowrap'
    },
    uploadButton: {
      backgroundColor: '#3b82f6',
      color: 'white'
    },
    removeButton: {
      backgroundColor: '#ef4444',
      color: 'white'
    },
    cancelButton: {
      backgroundColor: '#e5e7eb',
      color: '#374151'
    },
    disabledButton: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
      color: 'white'
    },
    successMessage: {
      padding: '0.625rem 0.875rem',
      backgroundColor: '#d1fae5',
      border: '1px solid #6ee7b7',
      borderRadius: '6px',
      color: '#047857',
      fontSize: '0.875rem',
      marginBottom: '0.75rem'
    },
    errorMessage: {
      padding: '0.625rem 0.875rem',
      backgroundColor: '#fee2e2',
      border: '1px solid #fca5a5',
      borderRadius: '6px',
      color: '#dc2626',
      fontSize: '0.875rem',
      marginBottom: '0.75rem'
    },
    hiddenInput: {
      display: 'none'
    }
  };

  return (
    <>
      {successMessage && (
        <div style={styles.successMessage}>
          ✓ {successMessage}
        </div>
      )}

      {errorMessage && (
        <div style={styles.errorMessage}>
          ✕ {errorMessage}
        </div>
      )}

      <div style={styles.container}>
        <span style={styles.label}>PDF Export Logo</span>

        {logoPreview ? (
          <img src={logoPreview} alt="Logo preview" style={styles.logoPreview} />
        ) : currentLogo ? (
          <img src={currentLogo} alt="Current logo" style={styles.logoPreview} />
        ) : (
          <div style={styles.noLogoPlaceholder}>📁</div>
        )}

        <div style={styles.buttonGroup}>
          {logoPreview ? (
            <>
              <button
                onClick={handleUpload}
                disabled={isSaving}
                style={{
                  ...styles.button,
                  ...(isSaving ? styles.disabledButton : styles.uploadButton)
                }}
                onMouseEnter={(e) => !isSaving && (e.target.style.backgroundColor = '#2563eb')}
                onMouseLeave={(e) => !isSaving && (e.target.style.backgroundColor = '#3b82f6')}
              >
                {isSaving ? 'Uploading...' : 'Save'}
              </button>
              <button
                onClick={handleCancelPreview}
                disabled={isSaving}
                style={{
                  ...styles.button,
                  ...styles.cancelButton
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#d1d5db'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#e5e7eb'}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <label style={{ ...styles.button, ...styles.uploadButton, cursor: 'pointer' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
              >
                {currentLogo ? 'Change' : 'Upload'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoSelect}
                  style={styles.hiddenInput}
                  disabled={isSaving}
                />
              </label>

              {currentLogo && (
                <button
                  onClick={handleRemove}
                  disabled={isSaving}
                  style={{
                    ...styles.button,
                    ...(isSaving ? styles.disabledButton : styles.removeButton)
                  }}
                  onMouseEnter={(e) => !isSaving && (e.target.style.backgroundColor = '#dc2626')}
                  onMouseLeave={(e) => !isSaving && (e.target.style.backgroundColor = '#ef4444')}
                >
                  {isSaving ? 'Removing...' : 'Remove'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default LogoUpload;
